import React, { useState, useEffect, useCallback } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../components/AuthContext";

// ============================================================================
// CARRIER LAYOUT — Mobile-First Bottom Tab Navigation
// Designed for truck drivers: one-handed operation, large touch targets,
// bottom nav with safe area insets, pull-to-refresh, dark theme
// ============================================================================

const CARRIER_TABS = [
  { path: "/app/carrier/assigned", icon: "🚛", label: "My Loads" },
  { path: "/app/carrier/board",    icon: "📋", label: "Board" },
  { path: "/app/carrier/tracking", icon: "📍", label: "Track" },
  { path: "/app/carrier/factoring",icon: "💵", label: "Pay" },
  { path: "/app/carrier/more",     icon: "☰",  label: "More" },
];

const MORE_ITEMS = [
  { path: "/app/carrier/profile",        icon: "👤", label: "My Profile" },
  { path: "/app/carrier/certifications", icon: "📜", label: "Certifications" },
  { path: "/app/carrier/carrier-alerts", icon: "🔔", label: "Load Alerts" },
  { path: "/app/carrier/oilfield",       icon: "🛢️", label: "Oilfield Board" },
  { path: "/app/carrier/credit-check",   icon: "💳", label: "Credit Check" },
];

function MoreMenu({ open, onClose }) {
  if (!open) return null;
  return (
    <>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
        zIndex: 998, backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
      }} />
      <div style={{
        position: "fixed", bottom: "calc(64px + env(safe-area-inset-bottom, 0px))",
        left: 8, right: 8, zIndex: 999,
        background: "#1e293b", borderRadius: 16, padding: "4px 0",
        boxShadow: "0 -8px 40px rgba(0,0,0,0.6)",
        border: "1px solid rgba(255,255,255,0.08)",
        animation: "carrier-slideUp 0.2s cubic-bezier(0.32,0.72,0,1)",
      }}>
        {MORE_ITEMS.map((item, i) => (
          <NavLink key={item.path} to={item.path} onClick={onClose} style={{
            display: "flex", alignItems: "center", gap: 14,
            padding: "15px 20px", color: "#e2e8f0",
            textDecoration: "none", fontSize: 16, fontWeight: 500,
            borderBottom: i < MORE_ITEMS.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none",
            WebkitTapHighlightColor: "transparent",
          }}>
            <span style={{ fontSize: 20, width: 28, textAlign: "center" }}>{item.icon}</span>
            {item.label}
          </NavLink>
        ))}

        <div style={{ padding: "10px 20px", borderTop: "1px solid rgba(255,255,255,0.1)" }}>
          <NavLink to="/app/broker" onClick={onClose} style={{
            display: "flex", alignItems: "center", gap: 14,
            color: "#64748b", textDecoration: "none", fontSize: 14,
            WebkitTapHighlightColor: "transparent",
          }}>
            <span style={{ fontSize: 18, width: 28, textAlign: "center" }}>↩️</span>
            Switch to Broker View
          </NavLink>
        </div>
      </div>
    </>
  );
}

export default function CarrierLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { carrierEmail } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);

  // Close menu on route change
  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  const isActive = (path) => {
    if (path === "/app/carrier/more") {
      return MORE_ITEMS.some(i => location.pathname.startsWith(i.path));
    }
    return location.pathname.startsWith(path);
  };

  // Hide bottom nav on detail/POD pages for max screen real estate
  const hideNav = /\/loads\/[^/]/.test(location.pathname);

  // Inject global carrier CSS once
  useEffect(() => {
    if (document.getElementById("carrier-layout-css")) return;
    const style = document.createElement("style");
    style.id = "carrier-layout-css";
    style.textContent = `
      @keyframes carrier-slideUp {
        from { transform: translateY(16px); opacity: 0; }
        to   { transform: translateY(0); opacity: 1; }
      }
      @keyframes carrier-fadeIn {
        from { opacity: 0; transform: translateY(4px); }
        to   { opacity: 1; transform: translateY(0); }
      }

      /* Smooth scroll for all carrier content */
      .carrier-scroll-root {
        -webkit-overflow-scrolling: touch;
        overscroll-behavior-y: contain;
        scroll-behavior: smooth;
      }
      .carrier-scroll-root::-webkit-scrollbar { display: none; }

      /* MOBILE: full-bleed content, bottom nav visible */
      @media (max-width: 767px) {
        .carrier-desktop-sidebar { display: none !important; }
        .carrier-content-area {
          margin-left: 0 !important;
          padding-bottom: calc(72px + env(safe-area-inset-bottom, 0px)) !important;
        }
        .carrier-content-area.nav-hidden {
          padding-bottom: 0 !important;
        }
        .carrier-bottom-nav.nav-hidden { display: none !important; }
      }

      /* DESKTOP: sidebar visible, bottom nav hidden */
      @media (min-width: 768px) {
        .carrier-bottom-nav { display: none !important; }
        .carrier-desktop-sidebar { display: flex !important; }
        .carrier-content-area {
          margin-left: 220px !important;
          padding-bottom: 0 !important;
        }
      }

      /* Active tab pulse on tap */
      .carrier-tab-btn:active {
        transform: scale(0.92);
      }
      .carrier-tab-btn {
        transition: transform 0.1s, color 0.15s;
      }
    `;
    document.head.appendChild(style);
  }, []);

  return (
    <div style={{ minHeight: "100vh", minHeight: "100dvh", background: "#0f172a" }}>

      {/* ══════════ DESKTOP SIDEBAR ══════════ */}
      <div className="carrier-desktop-sidebar" style={{
        display: "none", position: "fixed", top: 0, left: 0, bottom: 0, width: 220,
        background: "#1e293b", borderRight: "1px solid rgba(255,255,255,0.08)",
        flexDirection: "column", zIndex: 100,
      }}>
        <div style={{
          padding: "20px 16px 16px",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}>
          <div style={{ fontSize: 20, fontWeight: 900, color: "#ef4444", letterSpacing: -0.5 }}>
            RoadOptix
          </div>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>Carrier Portal</div>
        </div>
        <div style={{ flex: 1, padding: "8px 0", overflowY: "auto" }}>
          {[...CARRIER_TABS.filter(t => t.path !== "/app/carrier/more"), ...MORE_ITEMS].map(item => (
            <NavLink key={item.path} to={item.path} style={({ isActive: active }) => ({
              display: "flex", alignItems: "center", gap: 10,
              padding: "11px 16px", color: active ? "#ef4444" : "#94a3b8",
              textDecoration: "none", fontSize: 14, fontWeight: active ? 700 : 500,
              background: active ? "rgba(239,68,68,0.08)" : "transparent",
              borderRight: active ? "3px solid #ef4444" : "3px solid transparent",
              transition: "all 0.15s",
            })}>
              <span style={{ fontSize: 18 }}>{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </div>
        {carrierEmail && (
          <div style={{
            padding: "12px 16px", borderTop: "1px solid rgba(255,255,255,0.08)",
            fontSize: 11, color: "#475569",
          }}>
            {carrierEmail}
          </div>
        )}
      </div>

      {/* ══════════ PAGE CONTENT ══════════ */}
      <div className={`carrier-content-area carrier-scroll-root${hideNav ? " nav-hidden" : ""}`}
        style={{ minHeight: "100vh", minHeight: "100dvh", background: "#0f172a" }}
      >
        <Outlet />
      </div>

      {/* ══════════ MOBILE BOTTOM TAB NAV ══════════ */}
      <div className={`carrier-bottom-nav${hideNav ? " nav-hidden" : ""}`} style={{
        position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 900,
        background: "#1e293b",
        borderTop: "1px solid rgba(255,255,255,0.1)",
        display: "flex", alignItems: "stretch",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        boxShadow: "0 -2px 20px rgba(0,0,0,0.4)",
      }}>
        {CARRIER_TABS.map(tab => {
          const active = isActive(tab.path);
          const isMore = tab.path === "/app/carrier/more";

          return (
            <button
              key={tab.path}
              className="carrier-tab-btn"
              onClick={() => isMore ? setMoreOpen(!moreOpen) : navigate(tab.path)}
              style={{
                flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
                justifyContent: "center", gap: 2,
                padding: "10px 4px 8px", border: "none", cursor: "pointer",
                background: "transparent", position: "relative",
                color: active ? "#ef4444" : "#64748b",
                WebkitTapHighlightColor: "transparent",
                minHeight: 56,
              }}
            >
              {active && (
                <div style={{
                  position: "absolute", top: 0, left: "20%", right: "20%",
                  height: 3, background: "#ef4444", borderRadius: "0 0 4px 4px",
                }} />
              )}
              <span style={{ fontSize: 24, lineHeight: 1 }}>{tab.icon}</span>
              <span style={{
                fontSize: 10, fontWeight: active ? 700 : 500,
                letterSpacing: 0.2,
              }}>{tab.label}</span>
            </button>
          );
        })}
      </div>

      <MoreMenu open={moreOpen} onClose={() => setMoreOpen(false)} />
    </div>
  );
}
